import { Component, type ReactNode, Suspense } from "react";
import { Outlet } from "react-router-dom";
import i18n from "@/i18n";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { QAAlertBanner } from "./QAAlertBanner";
import { useUiStore } from "@store/uiStore";

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-blue border-t-transparent" />
    </div>
  );
}

// Catches errors thrown by page components so they don't unmount the
// sidebar/header shell.
class PageErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null };
  static getDerivedStateFromError(e: Error) { return { error: e }; }
  componentDidCatch(error: Error) {
    // Auto-reload on chunk load failures (stale assets after deploy)
    if (
      error.message.includes("dynamically imported module") ||
      error.name === "ChunkLoadError"
    ) {
      const reloadKey = "chunk_reload_" + window.location.pathname;
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
      }
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-6 text-sm text-red-400">
          <p className="font-semibold mb-1">{i18n.t("common.pageError")}</p>
          <pre className="whitespace-pre-wrap text-xs opacity-80">
            {(this.state.error as Error).message}
          </pre>
          <button
            className="mt-3 text-xs underline"
            onClick={() => window.location.reload()}
          >
            {i18n.t("common.tryAgain")}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function MainLayout() {
  const { sidebarCollapsed } = useUiStore();

  return (
    <div className="flex h-screen overflow-hidden bg-dark-bg">
      <Sidebar />
      <div
        className={`flex flex-1 flex-col overflow-hidden transition-all duration-200 ${
          sidebarCollapsed ? "ml-16" : "ml-64"
        }`}
      >
        <Header />
        <QAAlertBanner />
        <main className="flex-1 overflow-auto p-6">
          <PageErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </PageErrorBoundary>
        </main>
      </div>
    </div>
  );
}
